<template>
    <page class="ai-scan-page" :busy="busy">
        <div class="mb-4">
            <div class="mt-3">
                <b-row>
                    <b-col
                        cols="12"
                        md="6"
                        class="mb-3"
                    >
                        <b-card class="card-equal-height">
                            <div class="d-flex flex-column h-100">
                                <div class="d-flex align-items-center mb-3">
                                    <h4 class="mb-0 mr-2">Overall Average AI Property Score</h4>
                                    <fa-icon-tooltip
                                        id="property-overall-tooltip"
                                        :icon="['fas', 'circle-info']"
                                        class="text-dark"
                                        variant="dark"
                                        size="lg"
                                    >
                                        <p class="mb-0">
                                            Higher scores reflect listings with a greater attractiveness to potential guests as measured by correct grammar and spelling, and the quality and quantity of the marketing copy that is used to describe and market your property.
                                        </p>
                                    </fa-icon-tooltip>
                                </div>
                                <div class="d-flex justify-content-center mb-4">
                                    <AiScoreGraph width="150" height="150" :score="Math.round(averageScore || 0)" />
                                </div>
                                <div class="mt-auto p-3 rounded bg-light border">
                                    <div class="font-weight-bold mb-2">Score Breakdown</div>
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <span>
                                            <fa-icon :icon="['fas', 'ban']" class="text-danger mr-2" />
                                            Never Scanned
                                        </span>
                                        <b-badge variant="danger" pill>
                                            {{ properties.filter(p => !p.last_ai_scan).length }}
                                        </b-badge>
                                    </div>
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <span>
                                            <fa-icon :icon="['fas', 'exclamation-triangle']" class="text-warning mr-2" />
                                            Needing Attention
                                        </span>
                                        <b-badge variant="warning" pill>
                                            {{ properties.filter(p => p.last_ai_scan && Math.round(p.ai_score || 0) < 83).length }}
                                        </b-badge>
                                    </div>
                                    <div class="d-flex justify-content-between align-items-center">
                                        <span>
                                            <fa-icon :icon="['fas', 'star']" class="text-success mr-2" />
                                            Excellent Score
                                        </span>
                                        <b-badge variant="success" pill>
                                            {{ properties.filter(p => p.last_ai_scan && Math.round(p.ai_score || 0) >= 83).length }}
                                        </b-badge>
                                    </div>
                                </div>
                            </div>
                        </b-card>
                    </b-col>
                    <b-col
                        cols="12"
                        md="6"
                        class="mb-3"
                        v-if="aiScanTemplatePermission"
                    >
                        <b-card class="card-equal-height">
                            <div class="d-flex flex-column h-100">
                                <div class="d-flex align-items-center mb-2">
                                    <h4 class="mb-0 mr-2">Saved Replies & PDFs</h4>
                                </div>
                                <div class="text-muted small mb-3" style="max-width: 700px">
                                    The AI Scan reviews your saved replies and PDFs for spelling and grammar errors, enhancing the overall professionalism of your messages.
                                </div>

                                <div class="flex-grow-1 mt-2 mb-4 p-3 rounded bg-light border saved-replies-list">
                                    <div v-if="unlayerTemplates.length === 0" class="text-muted text-center py-3">
                                        No saved replies yet
                                    </div>
                                    <div v-else>
                                        <div v-for="template in unlayerTemplates" :key="template.id"
                                            class="template-item">
                                            <div class="d-flex flex-column">
                                                <span class="text-truncate mr-2">{{ template.name }}</span>
                                                <span class="text-muted small text-nowrap mr-2">
                                                    {{ formatDate(template.last_ai_scan) }}
                                                </span>
                                            </div>
                                            <b-button variant="primary" size="sm" @click="openUnlayerTemplate(template.id)">
                                                <font-awesome-icon icon="wand-magic-sparkles" />
                                            </b-button>
                                        </div>
                                    </div>
                                </div>

                                <div class="mt-auto">
                                    <b-button variant="primary" class="w-100" @click="openUnlayerTemplate()">
                                        View All
                                        <font-awesome-icon icon="chevron-right" class="ml-2" />
                                    </b-button>
                                </div>
                            </div>
                        </b-card>
                    </b-col>
                </b-row>
            </div>
        </div>

        <div class="mb-4">
            <h4>Properties</h4>
            <div class="text-muted small" style="max-width: 700px">
                The AI Scan checks your property for spelling and grammatical errors, inconsistent information, and the originality of your marketing copy. A quality score is then generated. This score is a tool to help improve your listing.  It is only visible to you.
            </div>

            <div class="carousel-row-wrapper position-relative">
                <button
                    v-if="showLeftArrow"
                    class="carousel-arrow left"
                    @click="scrollCarousel('left')"
                    aria-label="Scroll left"
                >
                    <font-awesome-icon icon="chevron-left" />
                </button>
                <button
                    v-if="showRightArrow"
                    class="carousel-arrow right"
                    @click="scrollCarousel('right')"
                    aria-label="Scroll right"
                >
                    <font-awesome-icon icon="chevron-right" />
                </button>
                <b-row
                    ref="carouselRow"
                    class="flex-nowrap overflow-auto"
                    style="flex-wrap: nowrap; padding: 0 20px;"
                >
                    <b-col
                        v-for="(property, index) in properties"
                        :key="property.id"
                        cols="auto"
                        class="mb-3 mt-3 d-flex"
                        style="min-width: 270px; max-width: 270px;"
                    >
                        <b-card class="property-card w-100">
                            <div class="icon-container mb-2 mt-1">
                                <fa-icon-tooltip
                                    :id="property.id + '-tooltip'"
                                    :icon="['fas', 'exclamation-circle']"
                                    class="text-danger"
                                    variant="danger"
                                    size="xl"
                                    v-if="!property.last_ai_scan"
                                >
                                    <p class="mb-0">Property never scanned by AI</p>
                                </fa-icon-tooltip>
                                <fa-icon-tooltip
                                    :id="property.id + '-tooltip'"
                                    :icon="['fas', 'triangle-exclamation']"
                                    class="text-warning"
                                    variant="warning"
                                    size="xl"
                                    v-else-if="Math.round(property.ai_score || 0) < 83"
                                >
                                    <p class="mb-0">Property Has a Low Score</p>
                                </fa-icon-tooltip>
                                <span v-else style="visibility: hidden;">
                                    <font-awesome-icon icon="circle" style="font-size: 1.7rem;" />
                                </span>
                            </div>
                            <div>
                                <div class="flex-shrink-0 mb-4">
                                    <AiScoreGraph :score="Math.round(property.ai_score || 0)" />
                                </div>
                                <div class="flex-grow-1 mb-2">
                                    <h5 class="mb-1 font-weight-bold text-truncate" :title="property.name">{{ property.name }}</h5>
                                    <div class="text-muted small mb-1">
                                        <font-awesome-icon icon="calendar-alt" class="mr-1" />
                                        {{ formatDate(property.last_ai_scan) }}
                                    </div>
                                </div>
                                <b-button
                                    v-if="aiScanPropertyPermission"
                                    variant="primary"
                                    size="sm"
                                    class="font-weight-bold px-4 mt-2"
                                    @click="goToAiScan(property.id)"
                                >
                                    {{ property.last_ai_scan ? 'Scan' : 'Get Started' }}
                                </b-button>
                            </div>
                        </b-card>
                    </b-col>
                </b-row>
            </div>
        </div>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import page from '../generic/page/page.vue';
import { AI_SCAN_ENDPOINT } from '../../endpoints';
import { formatDistance } from 'date-fns';
import AiScoreGraph from './ai-score-graph.vue';
import API from '../../api';
import FaIconTooltip from '../generic/fa-icon-tooltip/fa-icon-tooltip.vue';

export default {
    components: {
        page,
        AiScoreGraph,
        FaIconTooltip,
    },
    name: 'AiScanDashboard',
    data() {
        return {
            busy: false,
            properties: [],
            unlayerTemplates: [],
            showLeftArrow: false,
            showRightArrow: false,

            aiScanPropertyPermission: false,
            aiScanTemplatePermission: false,
        };
    },
    computed: {
        ...mapGetters('header', ['userCurrencySymbol']),
        averageScore() {
            if (!this.properties.length) return 0;
            const scores = this.properties.map(
                (p) => (typeof p.ai_score === 'number' && !isNaN(p.ai_score) ? p.ai_score : 0)
            );
            return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
        },
    },
    async mounted() {
        await this.fetchPermissions();
        await this.fetchProperties();
        await this.fetchTemplates();
        this.$nextTick(() => {
            this.updateCarouselArrows();
            const row = this.$refs.carouselRow?.$el || this.$refs.carouselRow;
            if (row) {
                row.addEventListener('scroll', this.updateCarouselArrows);
                window.addEventListener('resize', this.updateCarouselArrows);
            }
        });
    },
    beforeDestroy() {
        const row = this.$refs.carouselRow?.$el || this.$refs.carouselRow;
        if (row) {
            row.removeEventListener('scroll', this.updateCarouselArrows);
            window.removeEventListener('resize', this.updateCarouselArrows);
        }
    },
    methods: {
        handleBusy(busy) {
            this.busy = busy;
        },
        async fetchProperties() {
            try {
                this.busy = true;
                const response = await API.get(AI_SCAN_ENDPOINT() + 'rental-properties/');

                response.data.sort((a, b) => {
                    if (!a.last_ai_scan && b.last_ai_scan) return -1;
                    if (a.last_ai_scan && !b.last_ai_scan) return 1;
                    if (!a.last_ai_scan && !b.last_ai_scan) return 0;
                    return (a.ai_score || 0) - (b.ai_score || 0);
                });
                this.properties = response.data;
            } catch (error) {
                console.log(error);
                this.errorToast({ text: 'Could not load properties.' });
            } finally {
                this.busy = false;
            }
        },

        async fetchTemplates() {
            try {
                this.busy = true;
                const response = await API.get(AI_SCAN_ENDPOINT() + 'unlayer-templates/');

                response.data.sort((a, b) => {
                    if (!a.last_ai_scan && b.last_ai_scan) return -1;
                    if (a.last_ai_scan && !b.last_ai_scan) return 1;
                    if (!a.last_ai_scan && !b.last_ai_scan) return 0;
                    return (a.ai_score || 0) - (b.ai_score || 0);
                });
                this.unlayerTemplates = response.data;
            } catch (error) {
                console.log(error);
                this.errorToast({ text: 'Could not load properties.' });
            } finally {
                this.busy = false;
            }
        },

        async fetchPermissions() {
            try {
                this.busy = true;
                const response = await API.get(AI_SCAN_ENDPOINT() + 'permissions/');
                this.aiScanPropertyPermission = response.data.ai_scan_property;
                this.aiScanTemplatePermission = response.data.ai_scan_template;
            } catch (error) {
                console.log(error);
                this.errorToast({ text: 'Could not load permissions.' });
            } finally {
                this.busy = false;
            }
        },

        async goToAiScan(propertyId) {
            window.location.href = `/app/property/setup/details/${propertyId}?ai_scan=true`;
        },

        async openUnlayerTemplate(id=0) {
            window.location.href = '/app/saved-replies/' + id;
        },

        formatDate(dateString) {
            if (!dateString) return 'Never Scanned';
            const date = new Date(dateString);
            return 'Last Scan: ' + formatDistance(date, new Date(), { addSuffix: true });
        },

        scrollCarousel(direction) {
            const row = this.$refs.carouselRow?.$el || this.$refs.carouselRow;
            if (!row) return;
            const scrollAmount = 300;
            if (direction === 'left') {
                row.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
            } else {
                row.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            }
            setTimeout(this.updateCarouselArrows, 350);
        },
        updateCarouselArrows() {
            const row = this.$refs.carouselRow?.$el || this.$refs.carouselRow;
            if (!row) return;
            this.showLeftArrow = row.scrollLeft > 5;
            this.showRightArrow = row.scrollLeft + row.clientWidth < row.scrollWidth - 5;
        },
    },
};
</script>

<style lang="scss" scoped>
.ai-scan-page {
    padding: 15px;
}

.property-card {
    max-width: 250px;
    min-width: 250px;
}

:deep(.lx-page-container) {
    overflow-x: hidden;
}

.icon-container {
    min-height: 1rem;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    position: absolute;
}

.carousel-row-wrapper {
    position: relative;
}

.carousel-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    background: rgba(255, 255, 255, 0.95);
    border: 1.5px solid #bbb;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
    cursor: pointer;
    transition: background 0.2s, box-shadow 0.2s, border 0.2s, transform 0.18s;
    font-size: 1.45rem;
    color: #333;
}
.carousel-arrow.left {
    left: 0;
}
.carousel-arrow.right {
    right: 0;
}
.carousel-arrow:focus {
    outline: none;
    background: #e9ecef;
}

.card-equal-height {
    height: 100%;

    .card-body {
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    // Desktop and larger screens
    @media (min-width: 768px) {
        max-height: 390px !important;
        min-height: 390px;
    }

    // Mobile screens - allow natural height
    @media (max-width: 767px) {
        min-height: auto;
        max-height: none;

        .card-body {
            height: auto;
        }
    }
}

.saved-replies-list {
    overflow-y: auto;
    min-height: 120px;

    @media (min-width: 768px) {
        max-height: 100%;
    }

    @media (max-width: 767px) {
        max-height: 200px;
        min-height: 80px;
    }

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
        background: #ccc;
        border-radius: 3px;

        &:hover {
            background: #bbb;
        }
    }
}

.template-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    border-bottom: 1px solid #dee2e6;
    padding: 0.5rem;
    border-radius: 4px;

    &:last-child {
        border-bottom: none;
        margin-bottom: 0;
    }
}
</style>
